import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { convertRentalStatement } from '../../api/rentalConverterApi';
import { useFileRun } from '../../hooks/useFileRun';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the rental statement PDF (required).',
  'Upload the Excel template to fill (required).',
  'Optionally set an output filename, then hit Convert.',
  'The converted workbook downloads automatically once it is ready.',
];

interface RentalConverterPanelProps {
  mission: Mission;
  onBack: () => void;
}

function RentalConverterPanel({ mission, onBack }: RentalConverterPanelProps) {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [outputFilename, setOutputFilename] = useState('Rental_Statement_Converted.xlsx');
  const { status, error, result, fail, run } = useFileRun<Awaited<ReturnType<typeof convertRentalStatement>>>();

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);
  const [pdfDragActive, setPdfDragActive] = useState(false);
  const [templateDragActive, setTemplateDragActive] = useState(false);

  const handleSubmit = async () => {
    if (!pdfFile || !templateFile) {
      fail('Upload both the rental statement PDF and the Excel template before converting.');
      return;
    }
    await run(() => convertRentalStatement(pdfFile, templateFile, outputFilename), 'Conversion failed.');
  };

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">Rental statement PDF (required)*</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setPdfDragActive(true);
          }}
          onDragLeave={() => setPdfDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setPdfDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setPdfFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            pdfDragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={pdfDragActive ? { borderColor: mission.color } : undefined}
        >
          <Button
            onClick={() => pdfInputRef.current?.click()}
            style={{ backgroundColor: mission.color }}
            className="text-white hover:opacity-90"
          >
            <ArrowUpIcon /> Upload PDF statement
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">PDF only</span>
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf"
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setPdfFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {pdfFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {pdfFile.name}</span>
            <button
              type="button"
              onClick={() => setPdfFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove PDF file"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">Excel template (required)*</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setTemplateDragActive(true);
          }}
          onDragLeave={() => setTemplateDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setTemplateDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setTemplateFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            templateDragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={templateDragActive ? { borderColor: mission.color } : undefined}
        >
          <Button onClick={() => templateInputRef.current?.click()} variant="outline">
            <ArrowUpIcon /> Upload template
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">XLSX only</span>
          <input
            ref={templateInputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setTemplateFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {templateFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {templateFile.name}</span>
            <button
              type="button"
              onClick={() => setTemplateFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove template file"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="Rental_Statement_Converted.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Converting…' : 'Convert Statement'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Converting…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 flex flex-col gap-2">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
            ✅ {result.filename} downloaded.
          </div>
          {result.reconciliation && (
            <div
              className={`rounded-lg px-4 py-3 text-sm ${
                result.reconciliation.is_aligned
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {result.reconciliation.is_aligned
                ? `✅ Reconciliation aligned (tolerance ${result.reconciliation.tolerance}).`
                : `⚠️ Reconciliation mismatch (tolerance ${result.reconciliation.tolerance}) — review the checks in the workbook.`}
            </div>
          )}
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload a PDF statement and an Excel template above to get started.
        </div>
      )}
    </section>
  );
}

export default RentalConverterPanel;
